import type { ReactNode } from "react";
import { type CompanyFormat, formatMoney } from "../format/format";

/**
 * A list's filters (P8-57), kept in the URL as the API reads them:
 *
 * - `param=a,b`: is any of a or b; `not_param=a,b`: is none of them;
 * - `param=none`: is empty (no driver, no zone); `not_param=none`: is set.
 *
 * A field says which params it uses; the filter bar reads and writes them, so a filtered list can
 * be linked, survives a reload and goes back with the browser.
 */
export type FilterOperator = "any" | "none" | "empty" | "set";

export interface FilterOption {
  value: string;
  label: string;
  hint?: string;
}

interface FieldBase {
  id: string;
  label: string;
  /** The heading it is listed under in Add filter, e.g. "Order", "Delivery". */
  group: string;
  icon?: ReactNode;
}

export type FilterField = FieldBase &
  (
    | {
        kind: "options";
        param: string;
        options: FilterOption[];
        /** It can be empty (no driver yet): offers "is empty" and "is set". */
        nullable?: boolean;
        /** What "is empty" means here, e.g. "No driver yet". */
        emptyLabel?: string;
      }
    /** Whole days in the company's zone, as `from` and `to` instants. */
    | { kind: "dates"; from: string; to: string }
    /** A money range, as decimal strings. */
    | { kind: "amount"; min: string; max: string }
    /** On or off, e.g. "Late" (`late=true`). */
    | { kind: "flag"; param: string; value: string }
    /** Chosen elsewhere (a picker dialog); the chip shows `describe` and removes the params. */
    | { kind: "custom"; params: string[]; describe: (search: Search) => string | null }
  );

export type Search = Record<string, string | undefined>;

const not = (param: string) => `not_${param}`;

/** Every URL param a field uses, to clear it. */
export function paramsOf(field: FilterField): string[] {
  switch (field.kind) {
    case "options":
      return [field.param, not(field.param)];
    case "dates":
      return [field.from, field.to];
    case "amount":
      return [field.min, field.max];
    case "flag":
      return [field.param];
    case "custom":
      return field.params;
  }
}

export const isActive = (field: FilterField, search: Search) =>
  field.kind === "flag" ? search[field.param] === field.value : paramsOf(field).some((p) => Boolean(search[p]));

/** A cleared field, as a patch for the URL. */
export const clearOf = (field: FilterField): Search => Object.fromEntries(paramsOf(field).map((p) => [p, undefined]));

export interface OptionsValue {
  op: FilterOperator;
  values: string[];
}

/** An options field's operator and values, from the URL. */
export function readOptions(param: string, search: Search): OptionsValue | null {
  const any = search[param]?.split(",").filter(Boolean);
  const none = search[not(param)]?.split(",").filter(Boolean);
  if (any?.length) {
    const values = any.filter((v) => v !== "none");
    return values.length === 0 ? { op: "empty", values: [] } : { op: "any", values };
  }
  if (none?.length) {
    const values = none.filter((v) => v !== "none");
    return values.length === 0 ? { op: "set", values: [] } : { op: "none", values };
  }
  return null;
}

/** An options field's operator and values, for the URL. Nothing chosen clears it. */
export function writeOptions(param: string, value: OptionsValue): Search {
  const join = (values: string[]) => (values.length ? values.join(",") : undefined);
  switch (value.op) {
    case "any":
      return { [param]: join(value.values), [not(param)]: undefined };
    case "none":
      return { [param]: undefined, [not(param)]: join(value.values) };
    case "empty":
      return { [param]: "none", [not(param)]: undefined };
    case "set":
      return { [param]: undefined, [not(param)]: "none" };
  }
}

export interface ChipText {
  field: string;
  operator: string;
  value: string;
}

/** "Pending, Accepted +2": the first values by name, then how many more. */
function valueList(values: string[], options: FilterOption[]) {
  const labels = values.map((v) => options.find((o) => o.value === v)?.label ?? v);
  return labels.length <= 2 ? labels.join(", ") : `${labels.slice(0, 2).join(", ")} +${labels.length - 2}`;
}

/** "22 Oct" in the company's zone; the day before `to`, since `to` is the midnight after the last day. */
function day(iso: string, timezone: string, end = false) {
  const at = new Date(Date.parse(iso) - (end ? 1 : 0));
  return new Intl.DateTimeFormat("en-GB", { timeZone: timezone, day: "numeric", month: "short" }).format(at);
}

/** What a set filter's chip says, e.g. "Status · is any of · Pending, Accepted". Null when it isn't set. */
export function chipText(field: FilterField, search: Search, format: CompanyFormat): ChipText | null {
  switch (field.kind) {
    case "options": {
      const v = readOptions(field.param, search);
      if (!v) return null;
      const many = v.values.length > 1;
      const operator =
        v.op === "any" ? (many ? "is any of" : "is") : v.op === "none" ? (many ? "is none of" : "is not") : "";
      const value =
        v.op === "empty"
          ? (field.emptyLabel ?? "is empty")
          : v.op === "set"
            ? "is set"
            : valueList(v.values, field.options);
      return { field: field.label, operator, value };
    }
    case "dates": {
      const from = search[field.from];
      const to = search[field.to];
      if (!from && !to) return null;
      if (from && to) {
        const a = day(from, format.timezone);
        const b = day(to, format.timezone, true);
        return { field: field.label, operator: a === b ? "is" : "is between", value: a === b ? a : `${a} – ${b}` };
      }
      return from
        ? { field: field.label, operator: "is on or after", value: day(from, format.timezone) }
        : { field: field.label, operator: "is before", value: day(to as string, format.timezone, true) };
    }
    case "amount": {
      const min = search[field.min];
      const max = search[field.max];
      if (!min && !max) return null;
      const money = (v: string) => formatMoney(v, format);
      if (min && max) return { field: field.label, operator: "is between", value: `${money(min)} – ${money(max)}` };
      return min
        ? { field: field.label, operator: "is at least", value: money(min) }
        : { field: field.label, operator: "is at most", value: money(max as string) };
    }
    case "flag":
      return search[field.param] === field.value ? { field: field.label, operator: "", value: "" } : null;
    case "custom": {
      const value = field.describe(search);
      return value ? { field: field.label, operator: "is", value } : null;
    }
  }
}

/** The filters set in the URL, in the fields' order. */
export const activeFields = (fields: FilterField[], search: Search) => fields.filter((f) => isActive(f, search));

/** Every param any field uses, set or not: what "Clear all" clears. */
export const clearAll = (fields: FilterField[]): Search => Object.assign({}, ...fields.map(clearOf));

// --- views ----------------------------------------------------------------------------------------

/** A named set of filters: built in ("Today") or saved by the person. */
export interface ListViewDef {
  id: string;
  label: string;
  search: Search;
  saved?: boolean;
}

const clean = (s: Search) =>
  Object.fromEntries(
    Object.entries(s)
      .filter(([, v]) => v !== undefined && v !== "")
      .sort(([a], [b]) => a.localeCompare(b)),
  );

/** The view the URL is showing, if its filters are exactly one view's. */
export function currentView(views: ListViewDef[], search: Search, keys: string[]): ListViewDef | undefined {
  const pick = (s: Search) => clean(Object.fromEntries(keys.map((k) => [k, s[k]])));
  const now = JSON.stringify(pick(search));
  return views.find((v) => JSON.stringify(pick(v.search)) === now);
}

/** Where a person's saved views are kept: this browser, per list, company and person. */
export const viewsKey = (list: string, companyId: string, userId: string) =>
  `majitap:views:${list}:${companyId}:${userId}`;

export function readViews(key: string, storage: Pick<Storage, "getItem"> | undefined = globalThis.localStorage) {
  try {
    const raw = storage?.getItem(key);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed)
      ? parsed.filter(
          (v): v is ListViewDef =>
            typeof v?.id === "string" && typeof v?.label === "string" && typeof v?.search === "object",
        )
      : [];
  } catch {
    return [];
  }
}

export function writeViews(
  key: string,
  views: ListViewDef[],
  storage: Pick<Storage, "setItem"> | undefined = globalThis.localStorage,
) {
  try {
    storage?.setItem(key, JSON.stringify(views.map((v) => ({ id: v.id, label: v.label, search: clean(v.search) }))));
  } catch {
    // private mode or full storage: the view just isn't remembered
  }
}
